/**
 * NewDealQuickAction
 * -----------------------------------------------------------------------------
 * Atalho do FAB do chat para criar um novo Negócio (CRM). Reaproveita o
 * componente `DealFormDialog` já usado em `CrmDeals.tsx` — toda a criação
 * acontece server-side via as mesmas RPCs / validações.
 *
 * Regras de visibilidade (espelhadas 1:1 do botão original em CrmDeals.tsx):
 *  - O botão "Novo Negócio" na listagem (`CrmDeals.tsx` linha 754) só checa
 *    `!hideNewDealButton` — não há `canCreate`/`isAdmin` gating.
 *  - Como o FAB roda fora da rota CRM (que é protegida por `RequireModule`),
 *    aqui adicionamos a checagem de módulo ativo (`crm_enabled`).
 *
 * Retorna `null` quando o módulo está desativado para evitar pílula vazia
 * no FAB.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-03 v1.0.0 - Criação do atalho "Novo Negócio" no FAB do chat.
 */

import { useState } from 'react';
import { Briefcase } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { createLogger } from '@/lib/logger';

const logger = createLogger('NewDealQuickAction');

interface NewDealQuickActionProps {
  /** Contato pré-selecionado ao abrir o dialog (vem do chat). */
  contactId?: string;
}

export function NewDealQuickAction({ contactId }: NewDealQuickActionProps) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // Gate de módulo: FAB está fora da rota /crm/deals, então validamos aqui.
  if (!currentTenant?.crm_enabled) {
    return null;
  }

  return (
    <>
      <div className="rounded-full bg-background shadow-md ring-1 ring-border/50 hover:shadow-lg transition-shadow">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  logger.debug('Abrindo dialog de novo negócio', { contactId });
                  setOpen(true);
                }}
                aria-label="Novo Negócio"
                className="h-11 w-11 rounded-full flex items-center justify-center text-foreground hover:text-primary transition-colors"
              >
                <Briefcase className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Novo Negócio</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <DealFormDialog
        open={open}
        onOpenChange={setOpen}
        defaultContactId={contactId}
        onSuccess={() => {
          // Invalida listagens de negócios para refletir a criação.
          queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
        }}
      />
    </>
  );
}
